import { GripVertical, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/core/lib/utils';

interface WidgetCardHeaderProps {
  title: string;
  isEditing: boolean;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export function WidgetCardHeader({
  title,
  isEditing,
  isAdmin,
  onEdit,
  onDelete,
}: WidgetCardHeaderProps) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {isEditing && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="drag-handle cursor-grab touch-none text-muted-foreground hover:text-foreground"
                aria-label={t('widget.drag')}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('widget.drag')}</TooltipContent>
          </Tooltip>
        )}
        <h3 className={cn('truncate text-sm font-semibold', isEditing && 'text-muted-foreground')}>
          {title}
        </h3>
      </div>

      {isAdmin && !isEditing && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              aria-label={t('widget.menu.label', { title })}
              data-testid="dashboard-widget-menu-btn"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit} data-testid="dashboard-widget-edit-btn">
              <Pencil className="mr-2 h-4 w-4" />
              {t('widget.menu.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-red-600 focus:text-red-600"
              data-testid="dashboard-widget-delete-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('widget.menu.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
